import { DAY_COLORS, DAY_BG_LIGHT, DAY_BG_DARK, DAY_BORDER_LIGHT, DAY_BORDER_DARK, DAY_ACCENT } from '../constants/colors';
import { getWeatherStyle } from '../utils/textUtils';
import { findTourMatch } from '../utils/placeVerification';

export default function ItineraryDisplay({ content, spots, tourItems, onSpotClick, darkMode=false }) {
  const dm = darkMode;

  // 프롬프트 잔재 / 시스템 메시지 / 메타 주석 완전 제거 필터
  const BLOCKED_PATTERNS = [
    /===\s*[\w\s]+===/,
    /^\s*[\[{\]}`]/,
    /^```/,
    // EXAONE/Qwen의 ### 마크다운 헤더 마커
    /^#{1,6}\s*(ITINERARY|SPOTS|SUMMARY|PROFILE)[_\s]?(START|END)?\s*#*\s*$/i,
    /^#{1,6}\s*(날짜별\s*일정|성향\s*분석|여행자\s*정보|최종\s*일정|여행\s*컨셉|컨셉)\s*#*\s*$/,
    /^#{1,6}\s*[-=]{3,}\s*$/,
    /^(규칙|반드시|절대|You are|System|Assistant|User|Human|AI|Note|주의|지침|지시|형식|포맷|FORMAT|RULE|INSTRUCTION|OUTPUT)[\s:：]/i,
    /한국어로\s*(작성|답변|출력|생성)/,
    /중국어.*금지/,
    /중국어.*사용(하지|하면)/,
    /위\s*내용은\s*한국어/,
    /언어\s*(규칙|지시|설정)/,
    /\[.*언어.*\]/,
    /\[.*금지.*\]/,
    /\[.*규칙.*\]/,
    /\[.*반드시.*\]/,
    /\[.*중요.*\]/,
    /\[.*주의.*\]/,
    /^(여기|아래|위|다음|이제|자|그럼|그리고|참고로|참고|알림|안내)[는은이가]?\s*(여행|일정|계획|내용|결과)/,
    /^(생성|작성|완료|완성)된?\s*(여행|일정|계획)/,
    /^(Here is|Here's|Below is|The following|Note that|Please note|Important)/i,
    /^(I have|I will|I'll|Let me|As requested|As per|Based on)/i,
    /^\s*\*{3,}/,
    /^-{3,}$/,
    /汉字|漢字/,
    /chinese|mandarin/i,
    /\[language\s*=\s*ko\]/i,
    /\[LANGUAGE\]/i,
  ];

  const isBlockedLine = (line) => {
    const t = line.trim();
    if (!t) return false;
    return BLOCKED_PATTERNS.some(p => p.test(t));
  };

  const cleanLines = (() => {
    const lines = content.split('\n');
    const result = [];
    let inBlock = false;
    for (const line of lines) {
      const t = line.trim();
      if (/===.*START.*===/.test(t)) { inBlock = true; continue; }
      if (/===.*END.*===/.test(t))   { inBlock = false; continue; }
      if (inBlock) continue;
      if (isBlockedLine(line)) continue;
      result.push(line);
    }
    return result;
  })();

  // Day 헤더 감지 (다양한 형식 지원)
  // 📅 Day 1, ## Day 1, ### 1일차, **Day 2** 등 모두 매칭
  const isDayHeader = (line) => {
    const t = line.trim();
    // 📅 이모지 또는 다양한 Day 패턴
    return /^(?:📅|#{1,3}\s*)?(?:\*\*)?\s*(?:Day\s*\d+|DAY\s*\d+|\d+일차|\d+째날|첫째날|둘째날|셋째날|넷째날|다섯째날|여섯째날|일곱째날|여덟째날)/.test(t);
  };

  // 라인을 Day 단위로 그룹핑
  const dayGroups = (() => {
    const groups = [];
    let current = null;
    let dayCount = -1;
    for (const line of cleanLines) {
      if (isDayHeader(line)) {
        dayCount++;
        current = { lines: [line], dayIdx: dayCount };
        groups.push(current);
      } else {
        if (!current) {
          current = { lines: [line], dayIdx: -1 };
          groups.push(current);
        } else {
          current.lines.push(line);
        }
      }
    }
    return groups;
  })();

  const renderLine = (line, i, dayIdx) => {
    line = line.replace(/\*\*([^*]*)\*\*/g, '$1').replace(/\*\*/g, '').replace(/^#{1,6}\s*/, '');
    const accent = dayIdx >= 0 ? DAY_ACCENT[dayIdx % DAY_ACCENT.length] : "var(--accent)";
    const isDay = isDayHeader(line);
    const isWeather=line.match(/^🌤️|^🌧️|^❄️|^☀️\s*날씨:|날씨:/);
    const isWeatherTip=line.match(/^💡\s*날씨\s*TIP/);
    const isMorning=line.match(/^🌅/), isAfternoon=line.match(/^🌞/), isEvening=line.match(/^🌙/);
    const isMeal=line.match(/^🍽️/), isTransport=line.match(/^🚗|^🚇|^🚕|^🚙|^🚶|^🚉|^✈️|^🚌|^🛫|^🛬/);
    const isBreak=line.match(/^☕|^🛋️|^🧘|^🌴/);
    const isBudget=line.match(/^💰/), isClosing=line.match(/^💌/);
    const isSeparator=line.trim()==="---", isEmpty=line.trim()==="";
    const matchedSpot = spots?.find(s => s.name && line.includes(s.name));
    const verifiedTourItem = findTourMatch(matchedSpot, tourItems);
    const spotMeta = matchedSpot && (
      <span style={{ display:"inline-flex", alignItems:"center", gap:5, flexWrap:"wrap", marginLeft:4 }}>
        {verifiedTourItem && (
          <span
            title={`한국관광공사 관광 데이터: ${verifiedTourItem.title || verifiedTourItem.name}`}
            style={{ fontSize:10, color:dm?"#5EEAD4":"#0F766E", fontWeight:800 }}
          >
            ✅ 관광 데이터 확인
          </span>
        )}
        <span style={{ fontSize:10, color:"var(--accent)", fontWeight:700 }}>📍 지도 보기</span>
      </span>
    );
    const spotInteractionProps = matchedSpot ? {
      role: "button",
      tabIndex: 0,
      "aria-label": `${matchedSpot.name} 지도 보기${verifiedTourItem ? ", 실제 관광 데이터 확인됨" : ""}`,
      onClick: () => onSpotClick && onSpotClick(matchedSpot),
      onKeyDown: event => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSpotClick && onSpotClick(matchedSpot);
        }
      },
    } : {};

    if(isSeparator) return null;
    if(isEmpty) return <div key={i} style={{ height:4 }}/>;
    if(isDay) {
      // Day 번호만 추출 (예: "Day 1", "1일차", "첫째날")
      const dayNumMatch = line.match(/Day\s*(\d+)|(\d+)일차|(\d+)째날/);
      const koOrdMap = { "첫째날":1, "둘째날":2, "셋째날":3, "넷째날":4, "다섯째날":5, "여섯째날":6, "일곱째날":7, "여덟째날":8 };
      const koOrdMatch = Object.keys(koOrdMap).find(k => line.includes(k));
      const dayNum = dayNumMatch ? (dayNumMatch[1] || dayNumMatch[2] || dayNumMatch[3]) : (koOrdMatch ? koOrdMap[koOrdMatch] : (dayIdx + 1));
      // 📅 이모지 제거하고 본문만 추출
      const cleanLine = line.replace(/^📅\s*/, '').replace(/^Day\s*\d+\s*[·\-:–.]?\s*/i, '').replace(/^\d+일차\s*[·\-:–.]?\s*/, '').trim();
      return (
        <div key={i} style={{
          display:"flex", alignItems:"center", gap:10, marginBottom:12,
          paddingBottom:10,
          borderBottom:`2px solid ${accent}33`,
        }}>
          {/* Day 번호 배지 */}
          <div style={{
            minWidth:42, height:42, borderRadius:12,
            background:`linear-gradient(135deg, ${accent}, ${accent}cc)`,
            display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
            color:"#fff", fontWeight:900,
            boxShadow:`0 3px 10px ${accent}55`,
            flexShrink:0,
          }}>
            <span style={{ fontSize:9, lineHeight:1, opacity:0.9, letterSpacing:"0.05em" }}>DAY</span>
            <span style={{ fontSize:18, lineHeight:1.1 }}>{dayNum}</span>
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:15, fontWeight:900, color:accent, lineHeight:1.3 }}>
              {cleanLine || line}
            </div>
          </div>
        </div>
      );
    }
    if(isWeather) { const ws=getWeatherStyle(line,dm); return (
      <div key={i} style={{ margin:"4px 0", padding:"6px 11px", borderRadius:8, background:ws.bg, border:`1px solid ${ws.border}`, fontSize:12, fontWeight:700, color:dm?"rgba(226,232,240,0.92)":"rgba(30,41,59,0.88)", display:"flex", alignItems:"center", gap:7 }}>
        <span style={{ fontSize:15 }}>{ws.icon}</span><span>{line.replace(/^🌤️|^🌧️|^❄️|^☀️/,"").trim()}</span>
      </div>
    );}
    if(isWeatherTip) { const ws=getWeatherStyle(line,dm); return (
      <div key={i} style={{ margin:"3px 0 8px", padding:"8px 11px", borderRadius:8, background:ws.bg, border:`1px dashed ${ws.border}`, fontSize:12, color:dm?"rgba(226,232,240,0.88)":"rgba(30,41,59,0.88)", fontWeight:600 }}>
        <span style={{ color:dm?"#FBBF24":"#D97706", fontWeight:800 }}>💡 날씨 TIP  </span>{line.replace(/^💡\s*날씨\s*TIP\s*[:：]?\s*/,"")}
      </div>
    );}
    if(isMorning||isAfternoon||isEvening) {
      const timeColors={"🌅":"#ffa95c","🌞":"#ffe066","🌙":"#a78bfa"};
      const emoji=line.slice(0,2);
      return <div key={i} style={{ margin:"8px 0 2px", padding:"4px 10px", borderLeft:`3px solid ${timeColors[emoji]||accent}`, fontSize:13, fontWeight:800, color:timeColors[emoji]||accent }}>{line}</div>;
    }
    if(isMeal) return <div key={i} {...spotInteractionProps} className={matchedSpot?"itinerary-spot-line":undefined} style={{ margin:"2px 0", padding:"5px 10px", borderRadius:6, background:dm?"rgba(255,140,80,0.12)":"rgba(255,140,80,0.08)", border:dm?"1px solid rgba(255,140,80,0.28)":"1px solid rgba(255,140,80,0.16)", fontSize:12, color:dm?"#ffc090":"#c86030", cursor:matchedSpot?"pointer":"default" }}>{line}{spotMeta}</div>;
    if(isTransport) return <div key={i} style={{ margin:"2px 0 2px 16px", padding:"3px 10px", borderRadius:100, background:dm?"rgba(100,200,255,0.06)":"rgba(60,120,200,0.05)", border:dm?"1px dashed rgba(100,200,255,0.28)":"1px dashed rgba(60,120,200,0.22)", fontSize:11, color:dm?"#9ecff0":"#4070a0", fontStyle:"italic", display:"inline-block" }}>{line}</div>;
    if(isBreak) return <div key={i} style={{ margin:"4px 0", padding:"5px 11px", borderRadius:8, background:dm?"rgba(129,140,248,0.12)":"rgba(224,231,255,0.5)", border:dm?"1px dashed rgba(129,140,248,0.3)":"1px dashed rgba(99,102,241,0.3)", fontSize:12, color:dm?"#A5B4FC":"#4F46E5", fontWeight:600 }}>{line}</div>;
    if(isBudget||isClosing) return <div key={i} style={{ marginTop:8, padding:"8px 11px", borderRadius:8, background:dm?"rgba(129,140,248,0.1)":"rgba(238,242,255,0.95)", border:dm?"1px solid rgba(129,140,248,0.22)":"1px solid rgba(99,102,241,0.2)", fontSize:13, fontWeight:700, color:"var(--accent)" }}>{line}</div>;
    if(matchedSpot) return (
      <div key={i} {...spotInteractionProps} className="itinerary-spot-line" style={{ padding:"2px 10px", fontSize:12, color:dm?"rgba(226,232,240,0.85)":"rgba(51,65,85,0.82)", lineHeight:1.75, cursor:"pointer", borderRadius:6, transition:"background 0.15s, transform 0.12s" }}>
        {line}{spotMeta}
      </div>
    );
    return <div key={i} style={{ padding:"1px 10px", fontSize:12, color:dm?"rgba(226,232,240,0.80)":"rgba(51,65,85,0.80)", lineHeight:1.75 }}>{line}</div>;
  };

  return (
    <div style={{ background:dm?"rgba(15,23,42,0.6)":"rgba(252,253,255,0.9)", borderRadius:14, padding:"14px 14px 18px", border:dm?"1px solid rgba(99,102,241,0.18)":"1px solid rgba(99,102,241,0.18)", maxHeight:"56vh", overflowY:"auto", lineHeight:1.8, fontSize:14, display:"flex", flexDirection:"column", gap:10 }}>
      {dayGroups.map((group, gi) => {
        const { lines, dayIdx } = group;
        if (dayIdx < 0) {
          return <div key={gi}>{lines.map((line,i) => renderLine(line, i, -1))}</div>;
        }
        const bg     = dm ? DAY_BG_DARK[dayIdx % DAY_BG_DARK.length]         : DAY_BG_LIGHT[dayIdx % DAY_BG_LIGHT.length];
        const border = dm ? DAY_BORDER_DARK[dayIdx % DAY_BORDER_DARK.length]  : DAY_BORDER_LIGHT[dayIdx % DAY_BORDER_LIGHT.length];
        const accent = DAY_ACCENT[dayIdx % DAY_ACCENT.length];
        return (
          <div key={gi} style={{
            borderRadius:16,
            background:bg,
            border:`2px solid ${border}`,
            padding:"16px 14px 14px",
            boxShadow: dm
              ? `0 4px 16px ${accent}22, inset 0 1px 0 rgba(255,255,255,0.05)`
              : `0 4px 14px ${accent}1a, inset 0 1px 0 rgba(255,255,255,0.5)`,
            position:"relative",
            transition:"transform 0.2s, box-shadow 0.2s",
          }}>
            {lines.map((line,i) => renderLine(line, i, dayIdx))}
          </div>
        );
      })}
    </div>
  );
}
